'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from '@/lib/framer-motion-shim'
import { useI18n } from '@/lib/i18n/context'
import { temoinSessionPresent } from '@/lib/auth-securite'
import { Pictogram } from '@/components/Pictogram'
import DrapeauxLangue from '@/components/DrapeauxLangue'

/**
 * Un seul point d'entrée vers l'espace contributeur (demande de Guillaume du
 * 08/10/2026) : « Espace contributeur » mène à la page de présentation, qui
 * porte le formulaire de connexion dans son écran d'ouverture ; « Mon
 * espace » mène directement à /espace quand le témoin non secret
 * « sc-connecte » est présent (un compte équipe y est renvoyé vers son
 * espace par le serveur). Le témoin est lu dans le navigateur, sans
 * requête : les pages publiques ne dépendent pas de la session. Jamais de
 * lien vers l'espace équipe ni vers /admin.
 */
const URL_ESPACE = '/espace'

function IconeCompte({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg aria-hidden="true" focusable="false" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.8 20.2c1.1-3.6 3.9-5.4 7.2-5.4s6.1 1.8 7.2 5.4" />
    </svg>
  )
}

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [resourcesOpen, setResourcesOpen] = useState(false)
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false)
  const [profilsOpen, setProfilsOpen] = useState(false)
  const [mobileProfilsOpen, setMobileProfilsOpen] = useState(false)
  const [scrollState, setScrollState] = useState<'top' | 'partial' | 'full'>('top')
  const [connecte, setConnecte] = useState(false)
  const pathname = usePathname() || '/'
  const { t, locale } = useI18n()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const profilsRef = useRef<HTMLDivElement>(null)
  const profilsTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const isFr = locale !== 'en'

  const handleLanguageSwitch = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const href = toggleLocaleHref()
    window.location.href = href
  }

  useEffect(() => {
    setConnecte(temoinSessionPresent(document.cookie))
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      if (y >= 100) setScrollState('full')
      else if (y >= 10) setScrollState('partial')
      else setScrollState('top')
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setResourcesOpen(false)
      }
      if (profilsRef.current && !profilsRef.current.contains(e.target as Node)) {
        setProfilsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const scrolled = scrollState !== 'top'

  const localized = (path: string): string => {
    if (locale === 'en') {
      if (path === '/') return '/en'
      return `/en${path}`
    }
    return path
  }

  const toggleLocaleHref = (): string => {
    if (locale === 'en') {
      if (pathname === '/en') return '/'
      if (pathname.startsWith('/en/')) return pathname.slice(3)
      return '/'
    }
    if (pathname === '/') return '/en'
    return `/en${pathname}`
  }

  // « Vous êtes… » (accueil du 08/10/2026) : menu des trois publics, à la place
  // du lien « Entreprises » (qui en est la première entrée) — même nombre
  // d'éléments dans la barre.
  const profilsLinks = [
    { icon: 'building', name: isFr ? 'Une entreprise' : 'A company', desc: isFr ? 'Financer des projets labellisés' : 'Fund labelled projects', href: localized('/entreprises') },
    { icon: 'tree', name: isFr ? 'Propriétaire forestier' : 'A forest owner', desc: isFr ? 'Faire financer vos parcelles' : 'Get your plots funded', href: localized('/forestiers') },
    { icon: 'tractor', name: isFr ? 'Agriculteur' : 'A farmer', desc: isFr ? 'Valoriser vos pratiques bas-carbone' : 'Make the most of your practices', href: localized('/agricoles') },
  ]
  const profilsLabel = isFr ? 'Vous êtes…' : 'You are…'

  const navigation = [
    { name: t.nav.projects, href: localized('/projets') },
    // « Espace contributeur » n'est plus un lien de la barre : c'est l'accès
    // compte, à droite (un seul point d'entrée, 08/10/2026).
    { name: t.nav.impact, href: localized('/impact') },
    { name: t.nav.about, href: localized('/a-propos') },
  ]

  const resourcesLinks = [
    { icon: 'pen', name: isFr ? 'Blog' : 'Blog', desc: isFr ? 'Articles et actualités' : 'Articles and news', href: localized('/blog') },
    { icon: 'book', name: isFr ? 'Guides & Ressources' : 'Guides & Resources', desc: isFr ? 'Guides, outils, infographies' : 'Guides, tools, infographics', href: localized('/ressources') },
    { icon: 'book', name: isFr ? 'Glossaire carbone' : 'Carbon Glossary', desc: isFr ? 'Termes et définitions' : 'Terms and definitions', href: localized('/glossaire') },
    { icon: 'help', name: 'FAQ', desc: isFr ? 'Questions fréquentes' : 'Frequently asked questions', href: localized('/faq') },
    { icon: 'newspaper', name: isFr ? 'Ils parlent de nous' : 'Press', desc: isFr ? 'Articles de presse et médias' : 'Press articles and media coverage', href: localized('/ressources/presse') },
  ]


  const handleDropdownEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setResourcesOpen(true)
  }

  const handleDropdownLeave = () => {
    timeoutRef.current = setTimeout(() => setResourcesOpen(false), 150)
  }

  const resourcesPaths = ['/ressources', '/blog', '/glossaire', '/faq']
  const profilsPaths = ['/entreprises', '/forestiers', '/agricoles']

  const handleProfilsEnter = () => {
    if (profilsTimeoutRef.current) clearTimeout(profilsTimeoutRef.current)
    setProfilsOpen(true)
  }
  const handleProfilsLeave = () => {
    profilsTimeoutRef.current = setTimeout(() => setProfilsOpen(false), 150)
  }

  const isActive = (href: string) => {
    const cleanPath = pathname.replace(/^\/en/, '') || '/'
    const cleanHref = href.replace(/^\/en/, '') || '/'
    if (cleanHref === '/') return cleanPath === '/'
    // Ressources dropdown : actif si on est sur /blog, /glossaire, /faq ou /ressources
    if (cleanHref === '/ressources') {
      return resourcesPaths.some(rp => cleanPath === rp || cleanPath.startsWith(rp + '/'))
    }
    if (cleanHref === '/vous-etes') {
      return profilsPaths.some(rp => cleanPath === rp || cleanPath.startsWith(rp + '/'))
    }
    return cleanPath === cleanHref || cleanPath.startsWith(cleanHref + '/')
  }

  const presentationHref = localized('/espace-contributeur')
  const compteHref = connecte ? URL_ESPACE : presentationHref
  const compteLabel = connecte ? t.nav.myAccount : t.nav.contributorSpace
  // Sous le libellé, dans le menu mobile : ce qu'on y trouve
  const compteAide = connecte ? t.nav.contributorSpace : t.nav.login
  const compteActif = !connecte && isActive(presentationHref)

  const getLinkClass = (href: string) => {
    const active = isActive(href)
    return `text-sm transition-colors duration-200 relative
      after:absolute after:bottom-[-4px] after:left-0 after:h-0.5
      after:bg-amber after:transition-all after:duration-300 ${
        active
          ? 'font-bold after:w-full'
          : 'font-medium after:w-0 hover:after:w-full'
      } ${
        scrolled
          ? active ? 'text-forest' : 'text-gray-600 hover:text-forest'
          : active ? 'text-amber' : 'text-white hover:text-amber'
      }`
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-[400ms] ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
        scrollState === 'full'
          ? 'bg-white backdrop-blur-xl shadow-[0_1px_20px_rgba(0,0,0,0.06)] py-3'
          : scrollState === 'partial'
            ? 'bg-white/90 backdrop-blur-xl shadow-[0_1px_10px_rgba(0,0,0,0.03)] py-3'
            : 'bg-transparent py-5'
      }`}
    >
      <div className="container-site flex items-center justify-between">
        {/* Logo */}
        <Link href={localized('/')} className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-lg gradient-forest flex items-center justify-center text-white font-heading font-bold text-lg transition-transform duration-300 group-hover:scale-105 flex-shrink-0">
            S
          </div>
          <span className={`font-heading font-extrabold text-lg tracking-tight transition-colors duration-300 ${
            scrolled ? 'text-forest' : 'text-white'
          }`}>
            Stock <span className="font-extrabold">CO<sub className="text-[0.65em]">2</sub></span>
          </span>
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navigation.map((item, i) => (
            <span key={item.name} className={`contents`}>
              <Link href={item.href} className={getLinkClass(item.href)}>
                {item.name}
              </Link>
              {i === 0 && (
                <div
                  ref={profilsRef}
                  className="relative"
                  onMouseEnter={handleProfilsEnter}
                  onMouseLeave={handleProfilsLeave}
                >
                  <button
                    className={`${getLinkClass(localized('/vous-etes'))} flex items-center gap-1 cursor-pointer whitespace-nowrap`}
                    // Ouvre toujours (le survol l'a souvent déjà ouvert : basculer le refermerait) ;
                    // il se referme au départ de la souris, au clic ailleurs ou sur un lien.
                    onClick={() => setProfilsOpen(true)}
                    aria-expanded={profilsOpen}
                    aria-haspopup="true"
                    data-testid="menu-vous-etes"
                  >
                    {profilsLabel}
                    <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${profilsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <AnimatePresence>
                    {profilsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-3 w-72 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                      >
                        <div className="p-2">
                          {profilsLinks.map((link) => (
                            <Link
                              key={link.href}
                              href={link.href}
                              onClick={() => setProfilsOpen(false)}
                              className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-sage/40 transition-colors duration-150 group"
                            >
                              <span className="mt-0.5 text-forest"><Pictogram name={link.icon} className="w-5 h-5" /></span>
                              <div>
                                <div className="text-sm font-semibold text-forest">{link.name}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{link.desc}</div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </span>
          ))}

          {/* Ressources dropdown */}
          <div
            ref={dropdownRef}
            className="relative"
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleDropdownLeave}
          >
            <button
              className={`${getLinkClass(localized('/ressources'))} flex items-center gap-1 cursor-pointer`}
              onClick={() => setResourcesOpen(!resourcesOpen)}
              aria-expanded={resourcesOpen}
              aria-haspopup="true"
            >
              {isFr ? 'Ressources' : 'Resources'}
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <AnimatePresence>
              {resourcesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full right-0 mt-3 w-72 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                >
                  <div className="p-2">
                    {resourcesLinks.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setResourcesOpen(false)}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-sage/40 transition-colors duration-150 group"
                      >
                        <span className="mt-0.5"><Pictogram name={link.icon} className="w-5 h-5" /></span>
                        <div>
                          <div className="text-sm font-semibold text-forest group-hover:text-forest">
                            {link.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {link.desc}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        {/* CTA area */}
        <div className="hidden lg:flex items-center gap-4">
          <DrapeauxLangue
            locale={locale === 'en' ? 'en' : 'fr'}
            hrefAutre={toggleLocaleHref()}
            onBasculer={handleLanguageSwitch}
            surSombre={!scrolled}
          />
          {/* Espace contributeur (présentation et connexion) ou « Mon espace » :
              discret (lien, pas bouton plein). Icône seule entre 1024 et 1279 px,
              où la barre est la plus chargée. */}
          <a
            href={compteHref}
            aria-label={compteLabel}
            aria-current={compteActif ? 'page' : undefined}
            title={compteLabel}
            data-testid="header-espace"
            className={`group inline-flex items-center gap-2 h-9 text-sm rounded-full transition-colors duration-200 px-2 xl:px-1 ${
              compteActif ? 'font-bold' : 'font-medium'
            } ${
              scrolled
                ? compteActif ? 'text-forest' : 'text-gray-600 hover:text-forest'
                : compteActif ? 'text-amber' : 'text-white/90 hover:text-white'
            }`}
          >
            <span
              className={`flex items-center justify-center w-8 h-8 rounded-full border transition-colors duration-200 ${
                scrolled
                  ? 'border-gray-200 group-hover:border-forest group-hover:bg-sage/50'
                  : 'border-white/30 group-hover:border-white/60 group-hover:bg-white/10'
              }`}
            >
              <IconeCompte />
            </span>
            <span className="hidden xl:inline whitespace-nowrap">{compteLabel}</span>
          </a>
          <Link
            href={localized('/contact')}
            className={`text-sm px-5 py-2.5 rounded-full font-semibold transition-all duration-300 ${
              scrolled
                ? 'bg-forest text-white hover:bg-forest-light shadow-md hover:shadow-lg'
                : 'bg-white text-forest hover:bg-amber hover:text-white shadow-lg'
            }`}
          >
            {t.nav.contact}
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden w-10 h-10 flex items-center justify-center"
          aria-label={t.nav.menu}
          aria-expanded={isOpen}
        >
          <div className="flex flex-col gap-1.5">
            {[
              isOpen ? 'rotate-45 translate-y-2' : '',
              isOpen ? 'opacity-0' : '',
              isOpen ? '-rotate-45 -translate-y-2' : '',
            ].map((extra, i) => (
              <span
                key={i}
                className={`block w-6 h-0.5 transition-all duration-300 ${
                  scrolled ? 'bg-forest' : 'bg-white'
                } ${extra}`}
              />
            ))}
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="container-site py-6 flex flex-col gap-4">
              {navigation.map((item, i) => {
                const active = isActive(item.href)
                return (
                  <span key={item.name} className="contents">
                    <Link
                      href={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`font-heading text-lg py-2 border-b border-gray-50 ${
                        active ? 'font-extrabold text-amber' : 'font-semibold text-forest'
                      }`}
                    >
                      {item.name}
                    </Link>
                    {i === 0 && (
                      <div className="border-b border-gray-50">
                        <button
                          onClick={() => setMobileProfilsOpen(!mobileProfilsOpen)}
                          aria-expanded={mobileProfilsOpen}
                          className="w-full flex items-center justify-between font-heading font-semibold text-forest text-lg py-2"
                        >
                          {profilsLabel}
                          <svg className={`w-4 h-4 transition-transform duration-200 ${mobileProfilsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {mobileProfilsOpen && (
                          <div className="pl-4 pb-3 flex flex-col gap-2">
                            {profilsLinks.map((link) => (
                              <Link
                                key={link.href}
                                href={link.href}
                                onClick={() => { setIsOpen(false); setMobileProfilsOpen(false) }}
                                className="flex items-center gap-2 text-gray-600 hover:text-forest py-1.5"
                              >
                                <Pictogram name={link.icon} className="w-4 h-4" />
                                <span className="text-sm font-medium">{link.name}</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </span>
                )
              })}

              {/* Mobile Ressources accordion */}
              <div className="border-b border-gray-50">
                <button
                  onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                  className="w-full flex items-center justify-between font-heading font-semibold text-forest text-lg py-2"
                >
                  {isFr ? 'Ressources' : 'Resources'}
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${mobileResourcesOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <AnimatePresence>
                  {mobileResourcesOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 pb-3 flex flex-col gap-2">
                        {resourcesLinks.map((link) => (
                          <Link
                            key={link.href}
                            href={link.href}
                            onClick={() => { setIsOpen(false); setMobileResourcesOpen(false) }}
                            className="flex items-center gap-2 text-gray-600 hover:text-forest py-1.5"
                          >
                            <Pictogram name={link.icon} className="w-4 h-4" />
                            <span className="text-sm font-medium">{link.name}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Espace contributeur (présentation et connexion) ou « Mon espace » */}
              <a
                href={compteHref}
                onClick={() => setIsOpen(false)}
                aria-current={compteActif ? 'page' : undefined}
                data-testid="menu-mobile-espace"
                className="flex items-center gap-3 py-2 border-b border-gray-50 text-forest group"
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 group-hover:border-forest group-hover:bg-sage/50 transition-colors duration-200">
                  <IconeCompte className="w-[18px] h-[18px]" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className={`font-heading text-lg ${compteActif ? 'font-extrabold text-amber' : 'font-semibold'}`}>{compteLabel}</span>
                  <span className="text-xs text-gray-500">{compteAide}</span>
                </span>
              </a>

              <div className="flex items-center gap-4 pt-4">
                <DrapeauxLangue
                  locale={locale === 'en' ? 'en' : 'fr'}
                  hrefAutre={toggleLocaleHref()}
                  onBasculer={(e) => {
                    setIsOpen(false)
                    handleLanguageSwitch(e)
                  }}
                  avecLibelles
                />
                <Link
                  href={localized('/contact')}
                  onClick={() => setIsOpen(false)}
                  className="bg-forest text-white px-5 py-2.5 rounded-full font-semibold text-sm flex-1 text-center"
                >
                  {t.nav.contact}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
